import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { Alert, Box, Button, Form, Text } from '@factorialco/deprecated-design-system'
import type { Field } from 'factorial-form'
import { SettingPageEmployeesSettingFragment } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import Select from 'modules/core/components/Inputs/Select'
import TextInput from 'modules/core/components/Inputs/Text'
import TextField from 'modules/core/components/TextField'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import asyncAction from 'modules/core/lib/asyncAction'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'

// TODO: importing a private component from parent
import ManageSettingVersionModal from 'modules/attendance/components/SettingItemPage/TimeTracking/ManageSettingVersionModal'
import type BalanceCarryoverForm from 'modules/attendance/lib/forms/BalanceCarryoverForm'
import attendanceSettings from 'modules/attendance/store/collections/settings'
import { useTimeoffCompensations } from 'modules/attendance/store/collections/timeoffCompensation'
import type AttendanceSetting from 'modules/attendance/store/models/AttendanceSetting'
import BalanceCarryover, {
  RESET_FREQUENCIES,
  getResetFrecuencies,
} from 'modules/attendance/store/models/BalanceCarryover'
import { handleUpdateFrequencyI18n } from 'modules/timeoff/components/TimeoffCompensation/_Content/utils'

export const getResetFrequencyLabel = (type: string, hasBankOfHours?: boolean) => {
  switch (type) {
    case 'custom_period':
      return hasBankOfHours
        ? i18n.t('time_tracking.settings.balance.reset_frequency.custom')
        : i18n.t('time_tracking.settings.balance.reset_frequency.custom_period')
    case 'weekly':
      return i18n.t('time_tracking.settings.balance.reset_frequency.weekly')
    case 'monthly':
      return i18n.t('time_tracking.settings.balance.reset_frequency.monthly')
    case 'never':
      return i18n.t('time_tracking.settings.balance.reset_frequency.never')
    case 'yearly':
      return i18n.t('time_tracking.settings.balance.reset_frequency.yearly')
    default:
      throw new Error(`Unknown translation for type: ${type}`)
  }
}

const resetFrecuencyOptions = (hasBankOfHours: boolean) => {
  return getResetFrecuencies().map((resetFrequency) => ({
    label: getResetFrequencyLabel(resetFrequency ?? '', hasBankOfHours),
    value: resetFrequency,
  }))
}

const getResetFrecuencyPeriod = (type: string) => {
  switch (type) {
    case 'weekly':
      return i18n.t('time_tracking.settings.balance.reset_frequency_periods.weekly')
    case 'monthly':
      return i18n.t('time_tracking.settings.balance.reset_frequency_periods.monthly')
    case 'yearly':
      return i18n.t('time_tracking.settings.balance.reset_frequency_periods.yearly')
    default:
      return i18n.t('time_tracking.settings.balance.reset_frequency_periods.monthly') // default value
  }
}

export interface Props {
  attendanceSetting: AttendanceSetting
  balanceCarryover: BalanceCarryover
  form: BalanceCarryoverForm
  hasBankOfHours: boolean
}

const FormComponent = ({ attendanceSetting, balanceCarryover, form, hasBankOfHours }: Props) => {
  const [openEffectiveAtFormModal, setOpenEffectiveAtFormModal] = React.useState(false)
  const [running, setRunning] = React.useState(false)
  const options = { successMessage: i18n.t('common.saved_successfully') }
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const hasWeeklyBalanceCompensation = hasFeature('dev_time_tracking_weekly_balance_compensation')

  const compensationCollection = useTimeoffCompensations()
  const currentTimeoffCompensation = compensationCollection.last()

  const saveBalanceCarryover = async (
    balanceCarryover: BalanceCarryover,
    form: BalanceCarryoverForm,
    attendanceSettingId: number
  ) => {
    setRunning(true)

    await asyncAction(async () => {
      const { success, value } = await asyncAction(
        () => balanceCarryover.save(form.values, { patch: false }),
        options
      )

      if (success) {
        form.setValues({ reset_frequency: value.reset_frequency, ...value })
      }
    }, options)

    setOpenEffectiveAtFormModal(false)
    setRunning(false)
    form.cleanAll()

    await attendanceSettings.get(attendanceSettingId)?.fetch()

    compensationCollection.reset([])
    await compensationCollection.fetch({
      data: { attendance_employees_setting_id: attendanceSettingId },
    })
  }

  const onSubmit = async () => {
    saveBalanceCarryover(balanceCarryover, form, attendanceSetting.attributes.id)
  }

  const onCreateSetting = async (newSetting: SettingPageEmployeesSettingFragment) => {
    const settingId = +newSetting.id

    const attrs = {
      id: Number(newSetting.balanceCarryoverId) || 0,
      attendance_employees_setting_id: settingId,
    }

    const balanceCarryover = new BalanceCarryover(attrs)
    form.setValues(attrs)
    await saveBalanceCarryover(balanceCarryover, form, settingId)

    setOpenEffectiveAtFormModal(false)

    const routeParams = {
      id: 'time_tracking',
      settingId: settingId.toString(),
      tabId: 'compensation',
    }
    navigate(navigation.settings.time_tracking.settingId.tabId.fullPath(routeParams))
  }

  const onClose = () => {
    setOpenEffectiveAtFormModal(false)
  }

  const resetFrecuencyOnChange = (field: Field) => {
    if (field.value === 'yearly') form.get('frequency_number').set(1)
  }

  const formFrequency = form.get('reset_frequency').value

  const shouldUpdateOvertimeTimeoffCompensation = currentTimeoffCompensation
    ? (currentTimeoffCompensation.frequencyMonthly && formFrequency === RESET_FREQUENCIES.WEEKLY) ||
      (currentTimeoffCompensation.frequencyWeekly && formFrequency !== RESET_FREQUENCIES.WEEKLY)
    : false

  return (
    <Box
      flexDirection="column"
      gap="s16"
      background="grey200"
      padding="s24"
      borderRadius={{
        all: 'abs008',
      }}
    >
      <Form padding={'s0'}>
        <Box gap="s8">
          <Box gap="s4">
            <Text size="200" color="grey1000" weight="medium">
              {hasBankOfHours
                ? i18n.t('time_tracking.settings.balance.reset_frequency.title')
                : i18n.t('time_tracking.settings.balance.reset_balance')}
            </Text>
            {!hasBankOfHours && (
              <Text.Normal>
                {i18n.t('time_tracking.settings.balance.reset_balance_description')}
              </Text.Normal>
            )}
          </Box>
          <Select
            field={form.get('reset_frequency')}
            options={resetFrecuencyOptions(hasBankOfHours)}
            onChange={resetFrecuencyOnChange}
          />

          {!hasBankOfHours && shouldUpdateOvertimeTimeoffCompensation && form.isDirty && (
            <Alert color="warning">
              <Text.Normal weight="medium" size="300">
                {i18n.t(
                  'time_tracking.settings.balance.update_overtime_timeoff_compensation_warning',
                  {
                    balance_reset_frequency: getResetFrequencyLabel(formFrequency, hasBankOfHours),
                    ovetime_timeoff_compensation_frequency:
                      handleUpdateFrequencyI18n(formFrequency),
                  }
                )}
              </Text.Normal>
            </Alert>
          )}
        </Box>

        {form.showFrequencyNumber && !hasBankOfHours && (
          <Box flexDirection="column" gap="s4">
            <Text.Normal size="200" color="grey1000" weight="medium">
              {i18n.t('time_tracking.settings.balance.reset_frequency_number')}
            </Text.Normal>
            <Box flexDirection="row" gap="s8">
              <Box width="s96">
                <TextInput
                  field={form.get('frequency_number')}
                  mandatory
                  readonly={form.disableFrequencyNumber}
                />
              </Box>
              {hasWeeklyBalanceCompensation ? (
                <TextField
                  readonly
                  value={getResetFrecuencyPeriod(form.get('reset_frequency').value)}
                />
              ) : (
                <TextField
                  readonly
                  value={i18n.t('time_tracking.settings.balance.reset_frequency_number_default')}
                />
              )}
            </Box>
          </Box>
        )}

        {form.showFrequencyNumber && hasBankOfHours && (
          <Box flexDirection="row" gap="s8" alignItems="center">
            <Box>
              <Text.Normal size="200" color="grey1000" weight="medium">
                {i18n.t('time_tracking.settings.balance.reset_frequency.interval')}
              </Text.Normal>
              <Box width="s96">
                <TextInput
                  field={form.get('frequency_number')}
                  mandatory
                  readonly={form.disableFrequencyNumber}
                />
              </Box>
            </Box>
            <Box gap="s4">
              <Text.Normal size="200" color="grey1000" weight="medium">
                {i18n.t('time_tracking.settings.balance.period')}
              </Text.Normal>
              {hasWeeklyBalanceCompensation ? (
                <TextField
                  readonly
                  value={getResetFrecuencyPeriod(form.get('reset_frequency').value)}
                />
              ) : (
                <TextField
                  readonly
                  value={i18n.t('time_tracking.settings.balance.reset_frequency_number_default')}
                />
              )}
            </Box>
          </Box>
        )}

        {hasBankOfHours && (
          <Box flexDirection="row" gap="s4">
            <Text size="200" weight="bold">
              {i18n.t('time_tracking.settings.balance.reset_frequency.note')}
            </Text>
            <Text size="200">
              {i18n.t('time_tracking.settings.balance.reset_frequency.foot_note')}
            </Text>
          </Box>
        )}
        {form.isDirty && (
          <Box alignItems="flexEnd">
            <Button
              onClick={() => setOpenEffectiveAtFormModal(true)}
              disabled={!form.isValidFrequencyNumber}
            >
              {i18n.t('common.submit')}
            </Button>
          </Box>
        )}

        {openEffectiveAtFormModal && (
          <ManageSettingVersionModal
            onCreate={onCreateSetting}
            onUpdate={onSubmit}
            onClose={onClose}
            running={running}
            attendanceSettingId={attendanceSetting.attributes.id}
            attendanceSettingEffectiveAt={attendanceSetting.attributes.effective_at}
          />
        )}
      </Form>
    </Box>
  )
}

export default observer(FormComponent)
